/**
 * 軟體序號 (/licenses, boards/Licenses.dc.html) — admin+. Pool KPIs (GET /api/licenses/stats)
 * and a server-filtered, server-paged list (GET /api/licenses with page/limit) of MASKED codes.
 * Filters: search (bound SN / code tail / partner), state, batch, redemption date. Export is the
 * masked CSV of the current filter. A bound machine SN opens the lookup drawer.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Pager } from '@/components/Pager';
import { ToneTag } from '@/components/RosterBits';
import { MintModal, RevokeLicenseModal } from '@/components/LicenseDialogs';
import { errMsg } from '@/components/Dialogs';
import { useLookup } from '@/contexts/LookupContext';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { daysAgo } from '@/lib/dates';
import { fmtDay } from '@/lib/format';
import { useDebounced } from '@/lib/useDebounced';
import { SEARCH_TOO_LONG, searchTooLong } from '@/lib/searchLimit';
import { LICENSE_STATE_LABEL, LICENSE_STATE_TONE } from '@/lib/labels';
import type { LicenseBatch, LicenseListQuery, LicenseState, LicenseStats, Paged, SoftwareLicense } from '@/types';

const STATES: LicenseState[] = ['minted', 'activated', 'revoked'];
const RANGES = [
  { id: '', label: '不限' },
  { id: 'today', label: '今天' },
  { id: '7', label: '近 7 日' },
  { id: '30', label: '近 30 日' },
  { id: 'custom', label: '自訂…' },
];
const LIMIT = 100;

export function Licenses() {
  const [sp, setSp] = useSearchParams();
  const lookup = useLookup();
  const q = sp.get('q') ?? '';
  const state = (STATES as string[]).includes(sp.get('state') ?? '') ? (sp.get('state') as LicenseState) : undefined;
  const batch = sp.get('batch') ?? '';
  const range = sp.get('range') ?? '';
  const page = Math.max(1, Number(sp.get('page')) || 1);

  const [qInput, setQInput] = useState(q);
  const qDebounced = useDebounced(qInput.trim(), 350);
  const qTooLong = searchTooLong(qInput);
  useEffect(() => {
    if (searchTooLong(qDebounced)) return; // the Worker would 400; keep the last good search
    if (qDebounced !== q) set({ q: qDebounced || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  function set(patch: Record<string, string | null>, keepPage = false) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!keepPage) next.delete('page');
    setSp(next, { replace: true });
  }

  let activated_from: string | undefined;
  let activated_to: string | undefined;
  if (range === 'today') activated_from = daysAgo(0);
  else if (range === '7' || range === '30') activated_from = daysAgo(Number(range) - 1);
  else if (range === 'custom') {
    activated_from = sp.get('from') || undefined;
    activated_to = sp.get('to') || undefined;
  }

  const filter: Omit<LicenseListQuery, 'page' | 'limit'> = {
    q: q || undefined,
    state,
    batch_id: batch || undefined,
    activated_from,
    activated_to,
  };

  const list = usePoll<Paged<SoftwareLicense>>(() => api.listLicensesPaged({ ...filter, page, limit: LIMIT }), 30_000, [sp.toString(), lookup.changeTick]);
  const stats = usePoll<LicenseStats>(() => api.licenseStats(), 30_000);
  const batches = usePoll<LicenseBatch[]>(() => api.listLicenseBatches(), 120_000);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [mintOpen, setMintOpen] = useState(false);

  const s = stats.data;
  const rows = list.data?.entries ?? [];
  const refreshAll = () => { list.refresh(); stats.refresh(); batches.refresh(); };

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">軟體序號</h1>
          <p className="v2-sub">序號明碼是機密,列表一律遮罩;撤銷要輸入完整序號(出貨標籤或鑄造當下的匯出檔)</p>
        </div>
        <a className="btn btn-outline" href={api.licensesExportUrl(filter)} download>匯出目前結果(CSV,遮罩)</a>
        <Button className="btn-outline-danger" onClick={() => setRevokeOpen(true)}>撤銷序號…</Button>
        <Button variant="primary" onClick={() => setMintOpen(true)}>鑄造新批次</Button>
      </div>

      <div className="tiles c4">
        <div className="tile">
          <div className="tile-label">已鑄造</div>
          <div className="tile-value">{s?.minted ?? '—'}</div>
          <div className="tile-sub"><Link to="/licenses/batches" className="v2-back">{s ? `${s.batches} 個批次` : ' '}</Link></div>
        </div>
        <div className="tile"><div className="tile-label">已兌換</div><div className="tile-value tone-info">{s?.activated ?? '—'}</div><div className="tile-sub">每組綁一台機器</div></div>
        <div className="tile"><div className="tile-label">未兌換</div><div className="tile-value">{s?.unredeemed ?? '—'}</div><div className="tile-sub">還在出貨或庫存</div></div>
        <div className="tile"><div className="tile-label">已撤銷</div><div className="tile-value tone-danger">{s?.revoked ?? '—'}</div><div className="tile-sub">不可復原</div></div>
      </div>

      <div className="filterbar">
        <label className="v2-field" style={{ flex: 1, minWidth: 240 }}>搜尋
          <input className="v2-input" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="綁定機器序號、合作方" spellCheck={false} aria-invalid={qTooLong || undefined} />
          {qTooLong && <span className="error-line" role="alert">{SEARCH_TOO_LONG}</span>}
        </label>
        <label className="v2-field">狀態
          <select className="v2-select" value={state ?? ''} onChange={(e) => set({ state: e.target.value || null })}>
            <option value="">全部</option>
            {STATES.map((x) => <option key={x} value={x}>{LICENSE_STATE_LABEL[x]}</option>)}
          </select>
        </label>
        <label className="v2-field">批次
          <select className="v2-select" value={batch} onChange={(e) => set({ batch: e.target.value || null })}>
            <option value="">全部批次</option>
            {(batches.data ?? []).map((b) => <option key={b.batch_id} value={b.batch_id}>{b.batch_id}</option>)}
          </select>
        </label>
        <label className="v2-field">兌換日期
          <select className="v2-select" value={range} onChange={(e) => set({ range: e.target.value || null, from: null, to: null })}>
            {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
        {range === 'custom' && (
          <div className="v2-field">自訂範圍
            <div className="date-range">
              <input className="v2-input" type="date" aria-label="兌換開始日期" value={sp.get('from') ?? ''} onChange={(e) => set({ from: e.target.value || null })} />
              <span>–</span>
              <input className="v2-input" type="date" aria-label="兌換結束日期" value={sp.get('to') ?? ''} onChange={(e) => set({ to: e.target.value || null })} />
            </div>
          </div>
        )}
      </div>

      <section className="panel flush tpanel">
        {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(list.error)}</div>}
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr><th>序號</th><th>批次</th><th>合作方</th><th>狀態</th><th>綁定機器</th><th>鑄造日期</th><th>兌換日期</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={7}>{list.loading ? '載入中…' : '沒有符合的序號'}</td></tr>
              ) : (
                rows.map((l, i) => (
                  <tr key={`${l.code_masked}-${l.batch_id}-${l.minted_at ?? ''}-${i}`}>
                    <td className="mono muted">{l.code_masked || '••••'}</td>
                    <td className="mono">{l.batch_id || '—'}</td>
                    <td>{l.partner || '—'}</td>
                    <td><ToneTag tone={LICENSE_STATE_TONE[l.state]}>{LICENSE_STATE_LABEL[l.state] ?? l.state}</ToneTag></td>
                    <td>
                      {l.bound_machine_sn ? (
                        lookup.enabled
                          ? <button type="button" className="link mono" onClick={() => lookup.open(l.bound_machine_sn ?? '')}>{l.bound_machine_sn}</button>
                          : <span className="mono">{l.bound_machine_sn}</span>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="muted">{fmtDay(l.minted_at)}</td>
                    <td className="muted">{fmtDay(l.activated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} limit={LIMIT} total={list.data?.total ?? 0} unit="組" onPage={(n) => set({ page: n === 1 ? null : String(n) }, true)} />
      </section>

      {revokeOpen && <RevokeLicenseModal onClose={() => setRevokeOpen(false)} onDone={refreshAll} />}
      {mintOpen && <MintModal onClose={() => setMintOpen(false)} onMinted={refreshAll} />}
    </div>
  );
}
